import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useQueryClient } from "@tanstack/react-query";
import { useEdit } from "../../context/EditContext";
import { useMetadata } from "../../context/MetadataContext";
import useDevRenderLoopGuard from "../../../core/hooks/useDevRenderLoopGuard";
import { EditField } from "../EditField";
import { EditGroup } from "../EditGroup";
import { apiFetch } from "../../../core/utils/api";
import { generateEditUrl } from "../../../core/utils/urlUtils";
import { API_ENDPOINTS } from "../../navbar/constants";
import "../../styles.css";
import { useEditUI } from "../../context/EditUIPreferencesContext";
import { EditTabs } from "../EditTabs";
import { EditTabsWithFolders } from "../EditTabsWithFolders";
import AddElementPanel from "../addelements/AddElementPanel";
import WebsiteAnimationsPanel from "./WebsiteAnimationsPanel";
import ElementStylePresetsTab from "./ElementStylePresetsTab";
import { SUPPORTED_ELEMENT_STYLE_PRESET_TYPES } from "./elementStylePresetsApi";
import { getElementTypeLabel } from "../../utils/internalElementIcons";
import { TrashIcon, XIcon } from "../../../design-system/icons";
import { Modal, DangerButton, SecondaryButton, StatusBadge } from "../../../design-system";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";

const TYPE_TO_METADATA_KEY = {
  hero: "hero",
  title: "title",
  text: "text",
  image: "image",
  video: "video",
  button: "button",
  button_collection: "buttonCollection",
  file: "file",
  image_collection: "imageCollection",
  component: "component",
  navbar: "navbar",
  navbar_section: "navbarSection",
  sidebar: "sidebar",
  sidebar_section: "sidebarSection",
  footer: "footer",
  website_page: "page",
};

const EDIT_PANEL_RESIZE_GUTTER_CLASS = "sm:pr-6";
const PANEL_SWITCH_DURATION_SECONDS = 0.4;
const PANEL_SWITCH_EASE = [0.22, 0.61, 0.36, 1];

/**
 * A single, stable AnimatePresence wrapper that lives at a fixed position in
 * the React tree.  Changing `panelKey` triggers an exit → enter transition with
 * a quick flash + motion effect (~0.4 s).
 *
 * IMPORTANT: there must be exactly ONE instance of this component rendered by
 * ElementEditPanel (at the very end) so that AnimatePresence can track the
 * exiting and entering children properly.
 */
const LeftPanelSwitchTransition = ({ panelKey, children }) => {
  const prefersReducedMotion = useReducedMotion();
  const sweepDuration = prefersReducedMotion ? 0.15 : PANEL_SWITCH_DURATION_SECONDS;
  // Content swaps almost instantly; the light sweep is the real visual cue
  const contentDuration = prefersReducedMotion ? 0.1 : 0.1;

  return (
    <AnimatePresence initial={false} mode="wait">
      <motion.div
        key={panelKey}
        className="relative h-full min-h-0 overflow-hidden"
        initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
        animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1 }}
        exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0 }}
        transition={{ duration: contentDuration, ease: "easeOut" }}
      >
        {/* Light-sweep streak — light theme (blue on white) */}
        {!prefersReducedMotion ? (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-10 dark:hidden"
            style={{
              left: 0,
              width: "55%",
              background:
                "linear-gradient(100deg, rgba(59,130,246,0) 0%, rgba(59,130,246,0.18) 35%, rgba(147,197,253,0.28) 50%, rgba(59,130,246,0.12) 65%, rgba(59,130,246,0) 100%)",
            }}
            initial={{ x: "-100%", opacity: 0.7 }}
            animate={{ x: "210%", opacity: [0.7, 1, 0.7, 0] }}
            transition={{
              duration: sweepDuration,
              ease: [0.25, 0.46, 0.45, 0.94],
              opacity: { duration: sweepDuration, ease: "easeOut", times: [0, 0.3, 0.7, 1] },
            }}
          />
        ) : null}

        {/* Light-sweep streak — dark theme (white/light-blue with screen blend) */}
        {!prefersReducedMotion ? (
          <motion.div
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 z-10 hidden dark:block"
            style={{
              left: 0,
              width: "55%",
              background:
                "linear-gradient(100deg, rgba(255,255,255,0) 0%, rgba(186,216,255,0.45) 40%, rgba(255,255,255,0.18) 60%, rgba(255,255,255,0) 100%)",
              mixBlendMode: "screen",
            }}
            initial={{ x: "-100%", opacity: 0.6 }}
            animate={{ x: "210%", opacity: [0.6, 0.75, 0.6, 0] }}
            transition={{
              duration: sweepDuration,
              ease: [0.25, 0.46, 0.45, 0.94],
              opacity: { duration: sweepDuration, ease: "easeOut", times: [0, 0.3, 0.7, 1] },
            }}
          />
        ) : null}

        <div className="relative h-full min-h-0">{children}</div>
      </motion.div>
    </AnimatePresence>
  );
};

// ---------------------------------------------------------------------------
// Helpers shared by the "edit element" branches (only called when
// selectedElement + editMetadata are guaranteed to exist).
// ---------------------------------------------------------------------------

const toBool = (val) => {
  if (typeof val === "boolean") return val;
  if (typeof val === "number") return val !== 0;
  if (typeof val === "string") {
    return !["false", "0", "", "null", "undefined", "none"].includes(
      val.toLowerCase(),
    );
  }
  return Boolean(val);
};

const evalCondition = (cond, element) => {
  if (!cond) return null;
  let result = null;
  if (typeof cond === "string") {
    result = toBool(element[cond]);
  } else if (typeof cond === "object") {
    result = Object.entries(cond).every(([field, required]) => {
      const val = element[field];
      if (Array.isArray(required)) {
        return Array.isArray(val) && required.some((r) => val.includes(r));
      }
      if (required === null || required === undefined) {
        return val === null || val === undefined;
      }
      if (typeof required === "boolean") {
        return toBool(val) === required;
      }
      return String(val) === String(required);
    });
  }
  return result;
};

const shouldShowField = (metadata, element) => {
  const { show_if, hide_if } = metadata;
  const showResult = evalCondition(show_if, element);
  const hideResult = evalCondition(hide_if, element);
  return !(showResult === false || hideResult === true);
};

const isWebsiteLevelElement = (element) => {
  const websiteLevelTypes = [
    "navbar",
    "sidebar",
    "footer",
    "navbar_section",
    "sidebar_section",
    "website_page",
  ];
  return websiteLevelTypes.includes(element?.type);
};

const shouldShowDeleteButton = (element) => {
  const noDeleteTypes = ["navbar", "footer"];
  return !noDeleteTypes.includes(element?.type);
};

const formatType = (type) => {
  if (!type || typeof type !== "string") return "Component";
  const formatted = type.replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

const buildDeleteEndpoint = (item) => {
  const { type, id } = item;
  const itemTypes = [
    "title",
    "text",
    "image",
    "video",
    "button",
    "button_collection",
    "hero",
    "file",
  ];
  if (itemTypes.includes(type)) return `/api/items/${type}/${id}/delete/`;
  if (type === "component") return `/api/components/${id}/delete/`;
  if (type === "navbar_section") {
    const navbarId = item.navbar?.id || item.navbar;
    if (!navbarId) return null;
    return `/api/websites/${navbarId}/navbar/sections/${id}/delete/`;
  }
  if (type === "sidebar_section") {
    const sidebarId = item.sidebar?.id || item.sidebar;
    if (!sidebarId) return null;
    const isLeft = item.sidebar?.side === "left" || item.side === "left";
    const sidebarType = isLeft ? "leftsidebar" : "rightsidebar";
    return `/api/websites/${sidebarId}/${sidebarType}/sections/${id}/delete/`;
  }
  if (type === "image_collection") return `/api/imagecollections/${id}/delete/`;
  return null;
};

// ---------------------------------------------------------------------------
// ElementEditPanel
// ---------------------------------------------------------------------------

const ElementEditPanel = () => {
  useDevRenderLoopGuard("ElementEditPanel");
  const {
    selectedElement,
    editMetadata,
    clearSelection,
    updateField,
    deleteElement,
    websiteId,
    isAddMode,
    addModeTarget,
    exitAddMode,
    selectElement,
    editPanelFocusRequest,
    clearEditPanelFocusRequest,
  } = useEdit();
  const queryClient = useQueryClient();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const {
    groupViewMode,
    setSelectedTabPathForType,
    editPanelView,
    setEditPanelView,
  } = useEditUI();
  const containerRef = useRef(null);
  const metadataCtx = useMetadata();
  const [metadataRetryCount, setMetadataRetryCount] = useState(0);
  const [metadataRetryStopped, setMetadataRetryStopped] = useState(false);
  const [accordionPanelView, setAccordionPanelView] = useState("fields");

  const selectedId = selectedElement?.id || null;
  const selectedType = selectedElement?.type || null;
  const supportsStylePresets = SUPPORTED_ELEMENT_STYLE_PRESET_TYPES.has(selectedType);
  const metadataKey = selectedType ? TYPE_TO_METADATA_KEY[selectedType] : null;
  const refetchMetadataKeys = metadataCtx?.refetchMetadataKeys;

  const failureForSelectedType = useMemo(() => {
    if (!metadataKey) return null;
    if (!Array.isArray(metadataCtx?.error)) return null;
    return metadataCtx.error.find((f) => f?.key === metadataKey) || null;
  }, [metadataCtx?.error, metadataKey]);

  // Reset retry counters when the selection changes.
  useEffect(() => {
    if (!selectedElement) return;
    setMetadataRetryCount(0);
    setMetadataRetryStopped(false);
    setAccordionPanelView("fields");
  }, [selectedId, selectedType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry missing metadata so the user doesn't get stuck on "Preparing fields…".
  useEffect(() => {
    if (!selectedElement) return;
    if (editMetadata) return;
    if (!metadataKey) return;
    if (typeof refetchMetadataKeys !== "function") return;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;
    const intervalMs = 2500;
    let intervalId = null;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      setMetadataRetryCount(attempts);
      try {
        await refetchMetadataKeys([metadataKey], {
          reason: `element-edit-panel:missing:${selectedType}`,
        });
      } catch (_) {
        // Error surfaced via metadataCtx.error
      }
      if (attempts >= maxAttempts) {
        setMetadataRetryStopped(true);
        if (intervalId) {
          try {
            window.clearInterval(intervalId);
          } catch (_) {}
        }
      }
    };

    tick();
    intervalId = window.setInterval(tick, intervalMs);

    return () => {
      cancelled = true;
      try {
        window.clearInterval(intervalId);
      } catch (_) {}
    };
  }, [editMetadata, metadataKey, refetchMetadataKeys, selectedId, selectedType]);

  // Auto-dismiss the animations panel when the user NEWLY selects an element
  // or enters add-element mode while the animations panel is already open.
  // We track previous values so that simply *opening* the animations panel
  // while an element is already selected does NOT immediately revert it.
  const prevSelectedIdForDismissRef = useRef(selectedId);
  const prevIsAddModeForDismissRef = useRef(isAddMode);

  useEffect(() => {
    const prevSelId = prevSelectedIdForDismissRef.current;
    const prevAdd = prevIsAddModeForDismissRef.current;
    prevSelectedIdForDismissRef.current = selectedId;
    prevIsAddModeForDismissRef.current = isAddMode;

    if (editPanelView !== "animations") return;

    // A NEW element was selected while animations panel was open.
    const newSelection = selectedId != null && selectedId !== prevSelId;
    // Add-mode was just activated while animations panel was open.
    const newAddMode = isAddMode && !prevAdd;

    if (newSelection || newAddMode) {
      setEditPanelView("elements");
    }
  }, [selectedId, isAddMode, editPanelView, setEditPanelView]);

  // Keep panel state coherent: website-level animations view should not retain
  // add-mode in the background.
  useEffect(() => {
    if (editPanelView !== "animations") return;
    if (!isAddMode) return;
    exitAddMode();
  }, [editPanelView, isAddMode, exitAddMode]);

  // ── Callbacks ──────────────────────────────────────────────────────────────

  // Wrapped cancel handler: exits add mode AND signals the scaffold to switch
  // to preview on mobile. Separated from handleElementCreated which also calls
  // exitAddMode but should NOT switch to preview (element will be selected).
  const handleAddCancel = React.useCallback(() => {
    exitAddMode();
    window.dispatchEvent(new CustomEvent("editor:panel-dismissed"));
  }, [exitAddMode]);

  const handleElementCreated = async (newItem) => {
    exitAddMode();
    const refreshMeta = newItem?._refreshMeta || {};
    const { parentComponentId, collectionId, pageId, heroId } = refreshMeta;
    const createdType = newItem?.type;
    const createdId = newItem?.item_id || newItem?.id;

    await new Promise((resolve) => setTimeout(resolve, 200));

    if (parentComponentId) {
      queryClient.invalidateQueries({
        queryKey: ["component", parentComponentId],
      });
    }
    if (collectionId) {
      queryClient.invalidateQueries({
        queryKey: ["item", "button_collection", collectionId],
      });
    }
    if (heroId) {
      queryClient.invalidateQueries({ queryKey: ["item", "hero", heroId] });
    }
    if (pageId && websiteId) {
      queryClient.invalidateQueries({ queryKey: ["page-skeleton", websiteId] });
    }
    if (websiteId) {
      queryClient.invalidateQueries({ queryKey: ["website", websiteId] });
    }

    if (createdType && createdId) {
      queryClient.invalidateQueries({
        queryKey: ["item", createdType, createdId],
      });
      if (
        createdType === "button_collection" &&
        Array.isArray(newItem?.buttons)
      ) {
        newItem.buttons.forEach((b) => {
          if (b?.id)
            queryClient.invalidateQueries({
              queryKey: ["item", "button", b.id],
            });
        });
      }
    }

    await new Promise((resolve) => setTimeout(resolve, 200));

    if (createdType && createdId) {
      try {
        const hydrated = await queryClient.fetchQuery({
          queryKey:
            createdType === "component"
              ? ["component", createdId]
              : ["item", createdType, createdId],
          queryFn: async () => {
            const endpoint =
              createdType === "component"
                ? `/api/components/${createdId}`
                : `/api/items/${createdType}/${createdId}/`;
            const res = await apiFetch(endpoint);
            if (!res.ok) throw new Error("Failed to fetch created element");
            return res.json();
          },
        });
        if (hydrated) {
          selectElement(hydrated);
          return;
        }
      } catch (_) {
        // Fallback below
      }
      selectElement({ id: createdId, type: createdType });
    }
  };

  const handleChange = (field, value) => {
    updateField(field, value);
  };

  const handleDelete = () => {
    if (!selectedElement) return;
    if (selectedElement.type === "website_page") {
      if (!selectedElement.subdomain) return;
      setDeleteConfirmOpen(true);
      return;
    }
    deleteElement(selectedElement);
  };

  const handlePageDelete = async () => {
    if (!selectedElement || selectedElement.type !== "website_page") return;
    if (!selectedElement.subdomain) return;
    setDeleting(true);
    try {
      const response = await apiFetch(
        API_ENDPOINTS.PAGE_DELETE(websiteId, selectedElement.subdomain),
        { method: "DELETE", credentials: "include" },
      );
      if (!response.ok) {
        const txt = await response.text();
        throw new Error(txt || "Failed to delete page");
      }
      clearSelection();
      const currentPath = window.location.pathname;
      const pathParts = currentPath.split("/");
      if (pathParts.length >= 4) {
        const url = generateEditUrl(pathParts[2], pathParts[3], pathParts[4], "");
        window.location.href = url;
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error("Failed to delete page:", e);
      alert(e.message || "Failed to delete page");
    } finally {
      setDeleting(false);
      setDeleteConfirmOpen(false);
    }
  };

  const handleDeleteInternalItem = async (item) => {
    const typeLabel = getElementTypeLabel(item.type);
    const confirmed = window.confirm(
      `Are you sure you want to delete this ${typeLabel}?\n\n"${item.label || "Untitled"}"\n\nThis action cannot be undone.`,
    );
    if (!confirmed) return;
    try {
      const endpoint = buildDeleteEndpoint(item);
      if (!endpoint)
        throw new Error(`No delete endpoint found for type: ${item.type}`);
      const response = await apiFetch(endpoint, {
        method: "DELETE",
        credentials: "include",
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to delete item");
      }
      if (
        selectedElement?.id === item.id &&
        selectedElement?.type === item.type
      ) {
        clearSelection();
      }
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      console.error("Error deleting internal item:", error);
      alert(`Failed to delete ${typeLabel}: ${error.message}`);
    }
  };

  const handleEditInternalItem = (item) => {
    selectElement({ id: item.id, type: item.type });
  };

  // ── Focus requests from canvas ─────────────────────────────────────────────

  const focusRequestId = editPanelFocusRequest?.requestId;

  const focusEditFieldInPanel = (fieldName, { selectAll = false } = {}) => {
    const root = containerRef.current;
    if (!root || !fieldName) return false;
    const fieldEl = root.querySelector(`[data-edit-field="${fieldName}"]`);
    if (!fieldEl) return false;
    try {
      fieldEl.scrollIntoView({ block: "center", behavior: "smooth" });
    } catch (_) {
      fieldEl.scrollIntoView();
    }
    // Subtle border-like focus pulse — design-system aligned
    // borders.focus = blue-500 (light) / blue-400 (dark), applied at ~50 % for a neutral feel
    const isDark = fieldEl.closest(".dark") != null;
    fieldEl.style.setProperty(
      "--tw-ring-color",
      isDark
        ? "rgba(96, 165, 250, 0.5)"  // blue-400 @ 50 %
        : "rgba(59, 130, 246, 0.45)" // blue-500 @ 45 %
    );
    const pulseClasses = ["ring-1", "rounded-md"];
    pulseClasses.forEach((c) => fieldEl.classList.add(c));
    window.setTimeout(() => {
      pulseClasses.forEach((c) => fieldEl.classList.remove(c));
      fieldEl.style.removeProperty("--tw-ring-color");
    }, 800);
    const inputEl = fieldEl.querySelector(
      "textarea, input, [contenteditable='true'], [role='textbox']",
    );
    if (inputEl && typeof inputEl.focus === "function") {
      try {
        inputEl.focus({ preventScroll: true });
      } catch (_) {
        inputEl.focus();
      }
      if (selectAll) {
        if (typeof inputEl.select === "function") {
          inputEl.select();
        } else if (inputEl.isContentEditable) {
          const range = document.createRange();
          range.selectNodeContents(inputEl);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
        }
      }
    }
    return true;
  };

  useEffect(() => {
    if (!editPanelFocusRequest) return;
    if (!clearEditPanelFocusRequest) return;
    const { elementId, elementType, field, selectAll } = editPanelFocusRequest;
    if (
      !selectedElement ||
      selectedElement.id !== elementId ||
      selectedElement.type !== elementType
    ) {
      return;
    }
    if (!editMetadata) return;
    if (groupViewMode === "tabs" || groupViewMode === "folders") {
      setSelectedTabPathForType?.(elementType, [0]);
    } else {
      setAccordionPanelView("fields");
    }
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 12;
    const tryFocus = () => {
      if (cancelled) return;
      if (focusEditFieldInPanel(field, { selectAll })) {
        clearEditPanelFocusRequest();
        return;
      }
      attempts += 1;
      if (attempts >= maxAttempts) {
        clearEditPanelFocusRequest();
        return;
      }
      window.setTimeout(tryFocus, 50);
    };
    window.setTimeout(tryFocus, 0);
    return () => {
      cancelled = true;
    };
  }, [
    focusRequestId,
    editPanelFocusRequest,
    selectedElement,
    editMetadata,
    groupViewMode,
    setSelectedTabPathForType,
    clearEditPanelFocusRequest,
  ]);

  // ── Compute panel key + content (no early returns!) ────────────────────────
  //
  // A single LeftPanelSwitchTransition is rendered at the very bottom.
  // `panelKey` changes whenever the "mode" of the panel changes, which
  // triggers the exit → enter animation properly.

  let panelKey;
  let panelContent;

  if (editPanelView === "animations") {
    // ─── Website animations panel ───
    panelKey = "panel-animations";
    panelContent = (
      <WebsiteAnimationsPanel
        websiteId={websiteId}
        onClose={() => setEditPanelView("elements")}
      />
    );
  } else if (isAddMode && addModeTarget) {
    // ─── Add-element mode ───
    panelKey = "panel-add-mode";
    panelContent = (
      <div
        data-tutorial-id="edit-panel"
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
        <div className="flex-1 overflow-y-auto">
          <AddElementPanel
            target={addModeTarget}
            onElementCreated={handleElementCreated}
            onCancel={handleAddCancel}
          />
        </div>
      </div>
    );
  } else if (!selectedElement) {
    // ─── Nothing selected ───
    panelKey = "panel-empty";
    panelContent = (
      <div
        data-tutorial-id="edit-panel"
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
        <div
          className={`flex-1 flex items-center justify-center p-6 text-center ${textColors.muted}`}
        >
          Click on an element to edit its properties
        </div>
      </div>
    );
  } else if (!editMetadata) {
    // ─── Element selected but metadata still loading ───
    panelKey = "panel-loading-metadata";
    const canSoftReload =
      typeof refetchMetadataKeys === "function" && !!metadataKey;
    panelContent = (
      <div
        data-tutorial-id="edit-panel"
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div
              className={`flex justify-between items-center mb-6 sticky top-0 bg-transparent z-10 py-2 border-b ${borders.default}`}
            >
              <div className="flex flex-col">
                <h2 className={`${typography.h4} ${textColors.primary}`}>
                  Loading editor…
                </h2>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={clearSelection}
                  className={`${textColors.muted} hover:${textColors.secondary}`}
                >
                  ✕
                </button>
              </div>
            </div>
            <div className={`text-center ${textColors.muted}`}>
              Preparing fields…
            </div>
            <div className="mt-3 flex items-center justify-center gap-2">
              <StatusBadge
                variant={metadataRetryStopped ? "warning" : "info"}
                size="sm"
              >
                {metadataRetryStopped
                  ? "Still loading"
                  : metadataRetryCount > 0
                    ? `Retrying (${metadataRetryCount}/12)`
                    : "Waiting for metadata"}
              </StatusBadge>
            </div>
            {failureForSelectedType?.reason ? (
              <div className="mt-3 text-center text-sm text-red-600 dark:text-red-400">
                {String(failureForSelectedType.reason)}
              </div>
            ) : null}
            <div className="mt-5 flex items-center justify-center gap-2">
              <SecondaryButton
                onClick={() => {
                  if (!canSoftReload) return;
                  setMetadataRetryStopped(false);
                  setMetadataRetryCount(0);
                  refetchMetadataKeys([metadataKey], {
                    reason: "element-edit-panel:manual-reload",
                  });
                }}
                disabled={!canSoftReload}
              >
                Reload fields
              </SecondaryButton>
              <SecondaryButton onClick={() => window.location.reload()}>
                Refresh page
              </SecondaryButton>
            </div>
          </div>
        </div>
      </div>
    );
  } else {
    // ─── Element editor (metadata available) ───
    // Compute derived data from editMetadata + selectedElement.
    const mainFields = Object.entries(editMetadata.main_fields || {}).map(
      ([fieldName, fieldMetadata]) => ({
        field: fieldName,
        metadata: fieldMetadata,
      }),
    );

    const groupedFields = Object.entries(editMetadata).reduce(
      (groups, [groupName, groupData]) => {
        if (groupName === "main_fields") return groups;
        const fields = Object.entries(groupData.fields || {}).map(
          ([fieldName, fieldMetadata]) => ({
            field: fieldName,
            metadata: fieldMetadata,
          }),
        );
        groups[groupName] = {
          groupInfo: groupData.group_info,
          fields,
        };
        return groups;
      },
      {},
    );

    const visibleMainFields = mainFields.filter(({ metadata }) =>
      shouldShowField(metadata, selectedElement),
    );

    const pageHeaderTitle = (() => {
      if (selectedElement?.type !== "website_page") return null;
      const name =
        typeof selectedElement?.name === "string"
          ? selectedElement.name.trim()
          : selectedElement?.name != null
            ? String(selectedElement.name).trim()
            : "";
      const subdomain =
        typeof selectedElement?.subdomain === "string"
          ? selectedElement.subdomain.trim()
          : selectedElement?.subdomain != null
            ? String(selectedElement.subdomain).trim()
            : "";
      const location = subdomain ? `/${subdomain}` : "main page";
      const displayName = name || "Untitled page";
      return `Edit Page - ${displayName} (${location})`;
    })();

    const header = (
      <div className="p-6 pb-2">
        <div
          className={`flex justify-between items-center mb-1 sticky top-0 bg-transparent z-20 py-1.5 border-b ${borders.default}`}
        >
          <div className="flex flex-col min-w-0">
            <h2
              className={`${typography.h4} ${textColors.primary} truncate`}
              title={pageHeaderTitle || undefined}
            >
              {pageHeaderTitle || `Edit ${formatType(selectedElement.type)}`}
            </h2>
            {isWebsiteLevelElement(selectedElement) && (
              <div className="flex items-center mt-1">
                <StatusBadge variant="success" size="sm">
                  Website Level
                </StatusBadge>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-4">
            {shouldShowDeleteButton(selectedElement) && (
              <button
                onClick={handleDelete}
                className={`p-2 ${textColors.tertiary} hover:bg-red-500 hover:text-white rounded-md transition-colors`}
                title="Delete"
              >
                <TrashIcon size="sm" />
              </button>
            )}
            <button
              onClick={clearSelection}
              className={`${textColors.muted} hover:${textColors.secondary}`}
              title="Close"
            >
              <XIcon size="md" />
            </button>
          </div>
        </div>
      </div>
    );

    if (groupViewMode === "tabs") {
      panelKey = `panel-edit-tabs-${selectedId}`;
      panelContent = (
      <div
        data-tutorial-id="edit-panel"
        ref={containerRef}
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
          {header}
          <div className="flex-1 overflow-y-auto min-h-0">
            <EditTabs
              editMetadata={editMetadata}
              values={selectedElement}
              onChange={handleChange}
              onEditInternalItem={handleEditInternalItem}
              onDeleteInternalItem={handleDeleteInternalItem}
            />
          </div>
        </div>
      );
    } else if (groupViewMode === "folders") {
      panelKey = `panel-edit-folders-${selectedId}`;
      panelContent = (
      <div
        data-tutorial-id="edit-panel"
        ref={containerRef}
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
          {header}
          <div className="flex-1 min-h-0">
            <EditTabsWithFolders
              editMetadata={editMetadata}
              values={selectedElement}
              onChange={handleChange}
              onEditInternalItem={handleEditInternalItem}
              onDeleteInternalItem={handleDeleteInternalItem}
            />
          </div>
        </div>
      );
    } else {
      // Accordion mode (default)
      panelKey = `panel-edit-accordion-${selectedId}`;
      panelContent = (
      <div
        data-tutorial-id="edit-panel"
        ref={containerRef}
        className={`flex flex-col h-full ${surfaces.glassCard} ${EDIT_PANEL_RESIZE_GUTTER_CLASS}`}
      >
          {header}
          <div className="flex-1 overflow-y-auto min-h-0">
            <div className="p-6">
              {supportsStylePresets ? (
                <div className="mb-6 inline-flex rounded-lg border border-gray-200 bg-white p-1 dark:border-dark-700 dark:bg-dark-900">
                  <button
                    type="button"
                    onClick={() => setAccordionPanelView("fields")}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                      accordionPanelView === "fields"
                        ? "bg-blue-600 text-white"
                        : `${textColors.secondary} hover:bg-gray-100 dark:hover:bg-dark-800`
                    }`}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => setAccordionPanelView("styles")}
                    className={`rounded-md px-3 py-1.5 text-sm transition-colors ${
                      accordionPanelView === "styles"
                        ? "bg-blue-600 text-white"
                        : `${textColors.secondary} hover:bg-gray-100 dark:hover:bg-dark-800`
                    }`}
                  >
                    Presets
                  </button>
                </div>
              ) : null}

              {accordionPanelView === "styles" && supportsStylePresets ? (
                <ElementStylePresetsTab />
              ) : (
                <div className="space-y-6">
                  {visibleMainFields.length > 0 && (
                    <div className="mb-8">
                      <div className="space-y-4">
                        {visibleMainFields.map(({ field, metadata }) => (
                          <div
                            key={field}
                            data-edit-field={field}
                            className={`mb-2 pb-2 border-b ${borders.light} last:border-b-0`}
                          >
                            <EditField
                              field={field}
                              value={selectedElement[field]}
                              metadata={metadata}
                              allValues={selectedElement}
                              onChange={handleChange}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Object.entries(groupedFields).map(
                    ([groupName, groupData]) => (
                      <EditGroup
                        key={groupName}
                        groupName={groupName}
                        groupInfo={groupData.groupInfo}
                        fields={groupData.fields}
                        values={selectedElement}
                        onChange={handleChange}
                      />
                    ),
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Delete confirmation modal for website_page */}
          <Modal
            isOpen={
              deleteConfirmOpen && selectedElement?.type === "website_page"
            }
            onClose={() => setDeleteConfirmOpen(false)}
            title="Delete page"
          >
            <p className={`${textColors.secondary} mb-6`}>
              Are you sure you want to delete this page?
            </p>
            <div className="flex items-center justify-end space-x-3">
              <SecondaryButton onClick={() => setDeleteConfirmOpen(false)}>
                No
              </SecondaryButton>
              <DangerButton onClick={handlePageDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Yes, delete"}
              </DangerButton>
            </div>
          </Modal>
        </div>
      );
    }
  }

  // ── Single render point ────────────────────────────────────────────────────
  // One LeftPanelSwitchTransition lives at a fixed position in the React tree.
  // Changing panelKey properly triggers the exit → enter cycle.
  return (
    <LeftPanelSwitchTransition panelKey={panelKey}>
      {panelContent}
    </LeftPanelSwitchTransition>
  );
};

export default ElementEditPanel;
